import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper,
  Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import { apiHost } from '../../utils/apiHost';
import { RincianShift, cetakStrukShift } from './ShiftKasirWeb';
import { teksUmur } from '../../utils/shiftWaktu';

// Laporan Shift untuk admin cabang (shift cabangnya) dan superadmin (semua cabang).
// Server: controller/shiftController.js (GET /shift, GET /shift/:uuid, POST /shift/:uuid/tutup).

const base = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const ambil = (url) => axios.get(url, { withCredentials: true }).then((r) => r.data);
const rupiah = (v) => `Rp ${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;
const jam = (iso) => (iso ? new Date(iso).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-');
const angka = (s) => Number(String(s || '').replace(/[^0-9]/g, '')) || 0;
const ribuan = (s) => { const n = angka(s); return n ? n.toLocaleString('id-ID') : ''; };
const tgl = (d) => { const p = (x) => String(x).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };

const ChipSelisih = ({ s }) => {
  if (s.status !== 'tutup') return <Chip size="small" label="-" variant="outlined" />;
  if (s.selisih === null || s.selisih === undefined) return <Chip size="small" label="Tanpa hitungan" variant="outlined" />;
  if (s.selisih === 0) return <Chip size="small" color="success" label="Pas" />;
  return <Chip size="small" color={s.selisih < 0 ? 'error' : 'warning'} label={`${s.selisih < 0 ? '−' : '+'}${rupiah(Math.abs(s.selisih))}`} />;
};

const ChipStatus = ({ s }) => {
  // Shift lupa ditutup: tingkat & umur dari server (peringatan > 12 jam, terkunci > 24 jam: kasir tidak bisa transaksi sampai menutup).
  if (s.status === 'buka' && s.tingkat && s.tingkat !== 'normal') {
    return <Chip size="small" color={s.tingkat === 'terkunci' ? 'error' : 'warning'} label={`Terbuka ${teksUmur(s.umurJam || 0)}`} />;
  }
  if (s.status === 'buka') return <Chip size="small" color="info" label="Sedang buka" />;
  if (s.ditutupAdmin) return <Chip size="small" color="warning" variant="outlined" label="Ditutup admin" />;
  // Ditutup setelah melewati batas kunci: tetap ditandai supaya kelihatan di laporan.
  if (s.lamaJam && s.batas && s.lamaJam >= s.batas.kunciJam) return <Chip size="small" color="warning" variant="outlined" label={`Ditutup (${teksUmur(s.lamaJam)})`} />;
  return <Chip size="small" variant="outlined" label="Ditutup" />;
};

const LaporanShift = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const hariIni = useMemo(() => new Date(), []);
  const [dari, setDari] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 6); return tgl(d); });
  const [sampai, setSampai] = useState(() => tgl(hariIni));
  const [cabang, setCabang] = useState('');
  const [kasir, setKasir] = useState('');
  const [status, setStatus] = useState('');
  const [detail, setDetail] = useState(null);
  const [paksa, setPaksa] = useState(null);
  const [uang, setUang] = useState('');
  const [catatan, setCatatan] = useState('');
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');
  const [notif, setNotif] = useState(null);

  const query = new URLSearchParams({ dari, sampai, ...(cabang && { cabang }), ...(kasir && { kasir }), ...(status && { status }) }).toString();
  const { data, error, isLoading, mutate } = useSWR(`${base()}/shift?${query}`, ambil, { revalidateOnFocus: false });
  const { data: cabangData } = useSWR(superadmin ? `${base()}/cabangbyrole?bentuk=objek` : null, ambil);
  const { data: userData } = useSWR(`${base()}/${superadmin ? 'getuser' : 'getusercabang'}?bentuk=objek`, ambil);

  const daftarCabang = Array.isArray(cabangData?.data) ? cabangData.data : [];
  const daftarKasir = (Array.isArray(userData?.data) ? userData.data : [])
    .filter((u) => u.role === 'kasir' && (!cabang || u.cabanguuid === cabang));
  const list = Array.isArray(data?.data) ? data.data : [];
  const r = data?.ringkasan || {};

  const bukaDetail = async (s) => {
    setDetail({ ...s, memuat: true });
    try {
      const res = await axios.get(`${base()}/shift/${s.uuid}`, { withCredentials: true });
      setDetail(res.data.data);
    } catch (e) {
      setDetail(s);
    }
  };

  const tutupPaksa = async () => {
    setProses(true); setGalat('');
    try {
      await axios.post(`${base()}/shift/${paksa.uuid}/tutup`, {
        kasDihitung: uang === '' ? null : angka(uang),
        catatan: catatan.trim() || undefined,
      }, { withCredentials: true });
      setPaksa(null); setUang(''); setCatatan('');
      setNotif({ severity: 'success', text: 'Shift ditutup oleh admin' });
      mutate();
    } catch (e) {
      setGalat(e?.response?.data?.message || e?.message || 'Gagal menutup shift');
    } finally {
      setProses(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6">Laporan Shift</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {superadmin ? 'Semua shift kasir di semua cabang.' : 'Semua shift kasir di cabang Anda.'} Selisih = uang yang dihitung kasir − uang seharusnya (kas awal + penjualan tunai + kas masuk − kas keluar).
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField size="small" type="date" label="Dari" InputLabelProps={{ shrink: true }} value={dari} onChange={(e) => setDari(e.target.value)} />
        <TextField size="small" type="date" label="Sampai" InputLabelProps={{ shrink: true }} value={sampai} onChange={(e) => setSampai(e.target.value)} />
        {superadmin && (
          <TextField size="small" select label="Cabang" value={cabang} onChange={(e) => { setCabang(e.target.value); setKasir(''); }} sx={{ minWidth: 180 }}>
            <MenuItem value="">Semua cabang</MenuItem>
            {daftarCabang.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
          </TextField>
        )}
        <TextField size="small" select label="Kasir" value={kasir} onChange={(e) => setKasir(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">Semua kasir</MenuItem>
          {daftarKasir.map((u) => <MenuItem key={u.uuid} value={u.uuid}>{u.nama || u.username}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">Semua</MenuItem>
          <MenuItem value="buka">Sedang buka</MenuItem>
          <MenuItem value="tutup">Sudah ditutup</MenuItem>
        </TextField>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2 }}>
        {[
          ['Jumlah shift', r.jumlah ?? 0, 'text.primary'],
          ['Sedang buka', r.buka ?? 0, 'info.main'],
          ['Kas kurang', r.kurang ?? 0, 'error.main'],
          ['Kas lebih', r.lebih ?? 0, 'warning.dark'],
          ['Total selisih', `${(r.totalSelisih || 0) < 0 ? '−' : (r.totalSelisih || 0) > 0 ? '+' : ''}${rupiah(Math.abs(r.totalSelisih || 0))}`, (r.totalSelisih || 0) < 0 ? 'error.main' : 'text.primary'],
        ].map(([judul, nilai, warna]) => (
          <Card key={judul} variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{judul}</Typography>
              <Typography variant="h6" color={warna} fontWeight={700}>{nilai}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {(r.terbukaLama || 0) > 0 && (
        <Alert severity={(r.terkunci || 0) > 0 ? 'error' : 'warning'} sx={{ mb: 2, alignItems: 'center' }}
          action={<Button size="small" color="inherit" onClick={() => setStatus('buka')}>Lihat shift terbuka</Button>}>
          <b>{r.terbukaLama} shift terbuka terlalu lama</b> (lebih dari 12 jam)
          {(r.terkunci || 0) > 0 ? `, ${r.terkunci} di antaranya lebih dari 24 jam sehingga kasirnya tidak bisa bertransaksi sampai menutup shift` : ''}.
          Minta kasir menutup shift, atau gunakan Tutup paksa bila kasirnya sudah pulang.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error?.response?.data?.message || 'Gagal memuat laporan shift'}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {superadmin && <TableCell>Cabang</TableCell>}
              <TableCell>Kasir</TableCell>
              <TableCell>Dibuka</TableCell>
              <TableCell>Ditutup</TableCell>
              <TableCell align="right">Kas awal</TableCell>
              <TableCell align="right">Seharusnya</TableCell>
              <TableCell align="right">Dihitung</TableCell>
              <TableCell align="center">Selisih</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Aksi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((s) => (
              <TableRow key={s.uuid} hover>
                {superadmin && <TableCell>{s.cabang?.namacabang || '-'}</TableCell>}
                <TableCell>{s.kasir?.nama || '-'}</TableCell>
                <TableCell>
                  {jam(s.dibukaAt)}
                  {s.jamPerluDicek && (
                    <Tooltip title="Jam dari aplikasi offline perlu dicek (jam HP tidak cocok dengan server)">
                      <ScheduleOutlined fontSize="inherit" color="warning" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{jam(s.ditutupAt)}</TableCell>
                <TableCell align="right">{rupiah(s.kasAwal)}</TableCell>
                <TableCell align="right">{s.kasSeharusnya === null || s.kasSeharusnya === undefined ? '-' : rupiah(s.kasSeharusnya)}</TableCell>
                <TableCell align="right">{s.kasDihitung === null || s.kasDihitung === undefined ? '-' : rupiah(s.kasDihitung)}</TableCell>
                <TableCell align="center"><ChipSelisih s={s} /></TableCell>
                <TableCell><ChipStatus s={s} /></TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Button size="small" onClick={() => bukaDetail(s)}>Rincian</Button>
                  {s.status === 'buka' && <Button size="small" color="warning" onClick={() => { setPaksa(s); setUang(''); setCatatan(''); setGalat(''); }}>Tutup paksa</Button>}
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && list.length === 0 && (
              <TableRow><TableCell colSpan={superadmin ? 10 : 9} align="center">Belum ada shift pada rentang ini.</TableCell></TableRow>
            )}
            {isLoading && (
              <TableRow><TableCell colSpan={superadmin ? 10 : 9} align="center">Memuat...</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {list.length >= 300 && <Typography variant="caption" color="text.secondary">Menampilkan 300 shift terbaru. Persempit rentang tanggal untuk melihat yang lain.</Typography>}

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Rincian shift{detail?.kasir ? ` · ${detail.kasir.nama}` : ''}
          {detail?.cabang ? <Typography variant="caption" display="block" color="text.secondary">{detail.cabang.namacabang}</Typography> : null}
        </DialogTitle>
        <DialogContent dividers>
          {detail?.status === 'buka' && <Alert severity="info" sx={{ mb: 1.5 }}>Shift masih buka. Angka di bawah adalah perhitungan saat ini.</Alert>}
          {detail?.ditutupAdmin && <Alert severity="warning" sx={{ mb: 1.5 }}>Shift ini ditutup oleh admin, bukan oleh kasir.</Alert>}
          <RincianShift h={detail} />
        </DialogContent>
        <DialogActions>
          {detail?.status === 'tutup' && (
            <Button onClick={() => !cetakStrukShift(detail, detail?.kasir?.nama) && setNotif({ severity: 'info', text: 'Izinkan pop-up untuk mencetak struk' })}>Cetak struk</Button>
          )}
          <Button variant="contained" onClick={() => setDetail(null)}>Tutup</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(paksa)} onClose={() => !proses && setPaksa(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Tutup shift yang lupa ditutup</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {paksa && <Typography variant="body2" color="text.secondary">{paksa.kasir?.nama} · dibuka {jam(paksa.dibukaAt)} · kas awal {rupiah(paksa.kasAwal)}</Typography>}
          <TextField label="Uang di laci (opsional)" value={ribuan(uang)} onChange={(e) => setUang(e.target.value)} disabled={proses}
            InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>Rp</Typography> }}
            helperText="Kosongkan bila uang di laci tidak bisa dihitung; selisih tidak akan dicatat." />
          <TextField label="Catatan" value={catatan} onChange={(e) => setCatatan(e.target.value)} disabled={proses} multiline minRows={2}
            inputProps={{ maxLength: 500 }} placeholder="mis. kasir lupa menutup shift, dihitung ulang pagi hari" />
          {galat && <Alert severity="error">{galat}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaksa(null)} disabled={proses}>Batal</Button>
          <Button variant="contained" color="warning" onClick={tutupPaksa} disabled={proses}>{proses ? 'Menutup…' : 'Tutup shift'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(notif)} autoHideDuration={5000} onClose={() => setNotif(null)}>
        {notif ? <Alert severity={notif.severity} onClose={() => setNotif(null)}>{notif.text}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
};

export default LaporanShift;
